import { Capacitor, registerPlugin } from '@capacitor/core';
import { isPlatform } from '@ionic/react';
import urljoin from 'url-join';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { Dialog } from '@capacitor/dialog';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { FCM } from '@capacitor-community/fcm';
import { RateApp } from 'capacitor-rate-app';
import log from 'loglevel';

import fetch2, { METHOD } from '../fetch';
import getConfig from '../Config';

// REDIRECT_URL will be deprecated soon, please use APP_URL and urljoin instead
import { REDIRECT_URL, APP_ORIGIN, APP_URL, APP_SECURE_LOGIN_CALLBACK_URL } from './constant';
import * as storage from './CapacitorStorage';

const createElectronSkooldioCapacitorPlugin = () => ({
  isRootedDevice: () => window.electron.isRootedDevice(),
  isEmulator: () => window.electron.isEmulator(),
  isCaptured: () => window.electron.isCaptured(),
  isMultiScreen: () => window.electron.isMultiScreen(),
});

const SkooldioCapacitorPlugin = registerPlugin('SkooldioCapacitor', {
  electron: createElectronSkooldioCapacitorPlugin,
});
const CapacitorQRScanner = registerPlugin('CapacitorQRScanner');
const CapacitorRateApp = RateApp;
const isElectronPlatform = () => !!window.electron?.isElectron;

const createListenerCompatPlugin = (plugin) =>
  Object.assign(Object.create(plugin), {
    checkPermissions: (...args) => {
      if (isElectronPlatform()) {
        return Promise.resolve({ granted: false, receive: 'denied' });
      }

      return plugin.checkPermissions(...args);
    },
    requestPermissions: (...args) => {
      if (isElectronPlatform()) {
        return Promise.resolve({ granted: false, receive: 'denied' });
      }

      return plugin.requestPermissions(...args);
    },
    register: (...args) => {
      if (isElectronPlatform()) {
        return Promise.resolve();
      }

      return plugin.register(...args);
    },
    addListener: (eventName, listenerFunc) => {
      if (isElectronPlatform()) {
        return {
          remove: async () => {},
        };
      }

      const listenerHandlePromise = plugin.addListener(eventName, listenerFunc);
      return {
        remove: async () => {
          const listenerHandle = await listenerHandlePromise;
          return listenerHandle.remove();
        },
      };
    },
  });

const PushNotificationsCompat = createListenerCompatPlugin(PushNotifications);
const FCMPlugin = {
  ...FCM,
  getToken: (...args) => {
    if (isElectronPlatform()) {
      return Promise.resolve({ token: null });
    }

    return FCM.getToken(...args);
  },
};

const isNative = Capacitor.isNativePlatform();

const origin = APP_ORIGIN;
const appScheme = process.env.APP_SCHEME ?? 'capacitor';
const isElectron = () => !!window.electron?.isElectron;

const closeBrowser = async () => {
  if (isElectron()) {
    return window.electron.closeBrowser?.();
  }

  try {
    return await Browser.close();
  } catch (e) {
    console.warn('No browser found, skip', e.message);
  }
};

const resolveUserProvider = async (provider) => {
  if (!provider) {
    const providerFromStorage = await storage.getItem('provider');
    await storage.removeItem('provider');
    return providerFromStorage;
  } else {
    return provider;
  }
};

const resolveRedirectPath = async (redirectPath) => {
  if (!redirectPath) {
    const redirectPathFromStorage = await storage.getItem('redirectPath');
    await storage.removeItem('redirectPath');
    return redirectPathFromStorage || '';
  } else {
    return redirectPath;
  }
};

const resolveInternalRedirectPath = (redirectPath) => {
  const internalAppSchema = `${appScheme}:///`;
  const externalAppSchema = `${appScheme}://`;
  if (!redirectPath.includes(internalAppSchema) && redirectPath.includes(externalAppSchema)) {
    return redirectPath.replace(externalAppSchema, '');
  }
  return redirectPath;
};

const openInApp = async (url) => {
  // NOTE: For loading external URL in app local
  window.location.href = url;
};

const handleUrlOpen = async (getUserInfo, getUserProvider, handleRoutePath, url) => {
  const containUrl = APP_URL ? APP_URL : `${appScheme}://`;
  const containLoginUrl = APP_SECURE_LOGIN_CALLBACK_URL
    ? APP_SECURE_LOGIN_CALLBACK_URL
    : `${appScheme}://token/`;

  const { username } = getUserInfo();

  if (url.includes('username') && !url.includes(`username=${username}`) && url.includes('token')) {
    const [redirectPath, securePath] = url.split('?');
    const params = securePath.split('&').reduce((acc, param) => {
      const [key, value] = param.split('=');
      acc[key] = value;
      return acc;
    }, {});

    await storage.setItem('redirectPath', resolveInternalRedirectPath(redirectPath));

    loginWithToken(params);
  } else if (url.includes(containLoginUrl)) {
    await closeBrowser();
    const secureToken = url.split(containLoginUrl)[1];
    const provider = await resolveUserProvider(getUserProvider());
    const redirectPath = await resolveRedirectPath();

    secureLogin(secureToken.replace(/#.*/g, ''), provider).then(async (resp) => {
      if (resp) {
        // will change to use only goToLoginWithTokenPage
        // after migrate Anywhere login to use accessToken instead of cookies
        const { accessToken } = resp;
        if (accessToken && APP_URL) {
          goToLoginWithTokenPage(accessToken, redirectPath);
        } else {
          goToLandingPage('/' + redirectPath);
        }
      }
    });
  } else if (url.includes(containUrl)) {
    // MOE Book - Close browser
    await closeBrowser();
    const path = url.split(`${appScheme}://`).pop();
    const redirectPath = await resolveRedirectPath(path);
    handleRoutePath('/' + redirectPath);
  }
};

const routeListener = (getUserInfo, getUserProvider, incomingRoute = '', handleRoutePath) => {
  if (isNative && !isElectronPlatform()) {
    App.addListener('appUrlOpen', async (info) => {
      const { url } = info;
      await handleUrlOpen(getUserInfo, getUserProvider, handleRoutePath, url);
    });
  } else {
    const containUrl = `${appScheme}://`;
    const isContainUrl = incomingRoute.includes(containUrl);
    const url = isContainUrl ? incomingRoute : `${appScheme}://${incomingRoute}`;
    (async () => await handleUrlOpen(getUserInfo, getUserProvider, handleRoutePath, url))();
  }
};

const getDeviceIdInfo = async () => {
  const { identifier, uuid } = await Device.getId();
  return { uuid: uuid ?? identifier };
};

const alertWarningDialog = async (getUserInfo, warningInfo) => {
  const deviceInfo = await Device.getInfo();
  const uuid = await getDeviceIdInfo();
  const deviceInfoWithUUID = { ...deviceInfo, ...uuid };

  const userInfo = getUserInfo();
  const warningMessage = generateWarningMessage(userInfo, deviceInfoWithUUID, warningInfo);
  goToLandingPage();
  if (isElectron()) {
    await window.electron.alert?.(warningMessage);
  } else {
    await Dialog.alert(warningMessage);
  }
};

const getDeviceInfo = async () => {
  // keys of "Device" capacitor info which allow to be use in classroom platform
  const acceptFields = [
    'appVersion',
    'appBuild',
    'operatingSystem',
    'osVersion',
    'platform',
    'model',
    'manufacturer',
    'uuid',
    'isVirtual',
  ];
  const filteredDeviceInfo = {};

  const deviceInfo = await Device.getInfo();
  const uuid = await getDeviceIdInfo();
  let deviceInfoWithUUID = { ...deviceInfo, ...uuid };
  if (isNative && !isElectronPlatform()) {
    const { build, version } = await App.getInfo();
    deviceInfoWithUUID = { ...deviceInfoWithUUID, appVersion: version, appBuild: build };
  }

  Object.keys(deviceInfoWithUUID ?? {}).forEach((key) => {
    if (acceptFields.includes(key)) {
      if (!deviceInfoWithUUID[key] && deviceInfoWithUUID[key] !== false) {
        filteredDeviceInfo[key] = '-';
      } else {
        filteredDeviceInfo[key] = deviceInfoWithUUID[key];
      }
    }
    return;
  });

  // FIXME: For workaround appVersion from Capacitor when contextIsolation is true
  if (isElectron()) {
    filteredDeviceInfo.platform = window.electron.platform;
    filteredDeviceInfo.appVersion = await window.electron.getAppVersion();
  }

  return filteredDeviceInfo;
};

const isRootedDevice = async () => {
  if (isNative) {
    const resp = await SkooldioCapacitorPlugin.isRootedDevice();
    const { isRootedDevice } = resp || {};
    return isRootedDevice;
  }
  return false;
};

const isEmulator = async () => {
  if (isNative) {
    const resp = await SkooldioCapacitorPlugin.isEmulator();
    const { isEmulator } = resp || {};
    return isEmulator;
  }
  return false;
};

const isRecording = async () => {
  if (isNative) {
    const resp = await SkooldioCapacitorPlugin.isCaptured();
    const { isCaptured } = resp || {};
    return isCaptured;
  }
  return false;
};

const isMultiScreen = async () => {
  if (isNative) {
    const resp = await SkooldioCapacitorPlugin.isMultiScreen();
    const { isMultiScreen = false, screenCount } = resp || {};
    console.log('screenCount', screenCount);
    return isMultiScreen;
  }
  return false;
};

const goToLandingPage = (path) => {
  const redirectUrl = urljoin(
    APP_URL ? urljoin(APP_URL, '/login-loading') : REDIRECT_URL,
    path ? `?redirectPath=${path}` : ''
  );
  console.log('goToLandingPage', redirectUrl);
  window.location = redirectUrl;
};

const goToLoginWithTokenPage = (accessToken, path) => {
  const loginWithTokenUrl = urljoin(
    APP_URL,
    '/loginWithToken',
    `?token=${accessToken}`,
    `${path ? `&redirectUrl=${path}` : ''}`
  );
  window.location = loginWithTokenUrl;
};

const secureLogin = async (secureToken, provider) => {
  const {
    skooldioLogin: { passportProviders = [] },
  } = getConfig() || { skooldioLogin: {} };
  const passportProvider = passportProviders.find((p) => p.provider === provider);
  if (passportProvider) {
    const { secureLoginUrl, secureLoginAppId, secureLoginAppSecret } = passportProvider || {};
    return fetch2(secureLoginUrl, null, METHOD.POST, {
      appId: secureLoginAppId,
      appSecret: secureLoginAppSecret,
      secureToken: secureToken,
    });
  }
};

const loginWithToken = async (params) => {
  const { username, token } = params;
  const [provider] = username.split('.');
  const { userApi } = getConfig();
  await storage.setItem('provider', provider);
  const loginUrl = `${userApi}/loginWithToken?providerName=${provider}&token=${token}`;
  openUrl(loginUrl, () => {
    window.location = REDIRECT_URL;
    window.location = loginUrl;
  });
};

const generateWarningMessage = (userInfo, deviceInfo, warningInfo) => {
  const { firstname, lastname } = userInfo || {};
  const deviceInfoMessage = `หมายเลขเครื่อง (UUID): ${deviceInfo.uuid}\nรุ่น (Model): ${deviceInfo.model}\n`;

  const { title: warningTitle, message: warningMessage } = warningInfo;

  const message = `${
    firstname ? `คุณ ${firstname} ${lastname}\n` : ''
  }${deviceInfoMessage}${warningMessage}`;
  return {
    title: `คำเตือน ${warningTitle}`,
    message,
  };
};

/**
 * Opens a URL in the appropriate browser based on the platform.
 * @param {string} url The URL to open.
 * @param {object|Function} [options] Configuration options or a default function.
 * @param {boolean} [options.closeOther=false] - Whether to close any existing browser instance.
 * @param {boolean} [options.asBrowser=false] - (Electron) Open in the system's external browser.
 * @param {Function} [options.onClose] - Callback function executed when the browser is closed.
 * @param {string} [options.windowOptions] - Window options for web/cordova.
 */
const openUrl = async (
  url,
  options = {
    closeOther: false,
    asBrowser: false,
    defaultFunction: undefined,
    windowOptions: '',
    onClose: undefined,
  }
) => {
  if (isElectron()) {
    if (options.closeOther) {
      await closeBrowser();
    }

    if (typeof options === 'function' || options?.defaultFunction === 'function') {
      log.debug('Using default function');
      const defaultFunction = typeof options === 'function' ? options : options?.defaultFunction;
      defaultFunction();
      return;
    }

    if (options?.asBrowser || !options?.windowOptions) {
      log.debug('Opening URL in Electron browser');
      await window.electron.openBrowser(url);
      return;
    }

    log.debug('Opening URL in Electron child window');
    const win = window.open(url, '_blank', options?.windowOptions);
    if (win && typeof options?.onClose === 'function') {
      const timer = setInterval(() => {
        if (win.closed) {
          clearInterval(timer);
          options.onClose();
          log.debug('Electron: Window closed');
        }
      }, 500);
    }
    return;
  }

  if (isNative) {
    if (options.closeOther) {
      await closeBrowser();
    }
    await Browser.open({ url });
    // NOTE: Add listener to detect when browser is closed
    // NOTE: This listener will only work once because we will remove it after it's done
    const listener = await Browser.addListener('browserFinished', () => {
      log.debug('Native: Browser finished');
      if (typeof options?.onClose === 'function') {
        options.onClose();
      }
      // NOTE: Remove listener when it's done
      listener.remove();
    });
  } else if (isPlatform('electron') && options?.asBrowser) {
    log.debug('Opening URL in Electron browser');
    await window.electron.openBrowser(url);
    // NOTE: Electron version 14 can not use App.openUrl to open external URL
    // const response = App.openUrl({ url });
  } else if (typeof options === 'function' || options?.defaultFunction === 'function') {
    log.debug('Using default function');
    const defaultFunction = typeof options === 'function' ? options : options?.defaultFunction;
    defaultFunction();
  } else {
    log.debug('Opening URL in new window');
    const win = window.open(url, '_blank', options?.windowOptions);

    // NOTE: Detect close on web or electron
    if (win && typeof options?.onClose === 'function') {
      const timer = setInterval(() => {
        if (win.closed) {
          clearInterval(timer);
          options.onClose();
          log.debug('Web or Electron: Window closed');
        }
      }, 500);
    }
  }
};

const scanBarcode = async () => {
  const result = await CapacitorQRScanner.scan();
  return result.code;
};

const alertTimeOut = async () => {
  // const warningMessage = generateTimeOutMessage();
  const message = {
    title: 'เวลาหมด',
    message: 'เวลาของคอร์สนี้หมดแล้ว น้องสามารถติดต่อสถาบันผู้สอนเพื่อซื้อเวลาเพิ่มได้',
  };
  if (isElectron()) {
    await window.electron.alert?.(message);
  } else {
    await Dialog.alert(message);
  }
  goToLandingPage();
};

const requestAppReview = () => {
  if (isNative) CapacitorRateApp.requestReview();
};

const checkiOSSupportVersion = async () => {
  const { osVersion } = await getDeviceInfo();
  if (parseFloat(osVersion) > 13.0 && parseFloat(osVersion) < 13.6) {
    const message = {
      title: 'กรุณาอัปเดตเวอร์ชันเป็น iOS หรือ ipadOS 13.6',
      message: `
      น้องกำลังใช้งานบน device iOS หรือ ipadOS version ${osVersion}
      ซึ่งอาจะเกิดปัญหาภาพไม่ตรงกับเสียง
      เพื่อแก้ปัญหาดังกล่าว น้องสามารถอัปเดตให้เป็นเวอร์ชั่น iOS 13.6
      เข้าไปที่ Settings > General > Software Update`,
    };
    if (isElectron()) {
      await window.electron.alert?.(message);
    } else {
      await Dialog.alert(message);
    }
  }
};

export {
  isRecording,
  isMultiScreen,
  alertWarningDialog,
  isNative,
  openUrl,
  scanBarcode,
  alertTimeOut,
  isRootedDevice,
  isEmulator,
  origin,
  routeListener,
  requestAppReview,
  getDeviceInfo,
  goToLandingPage,
  checkiOSSupportVersion,
  PushNotificationsCompat as PushNotifications,
  FirebaseAnalytics,
  FCMPlugin,
  openInApp,
};
